import { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Form, Input, Switch, Select, InputNumber, Button, Space, Alert, Spin } from 'antd';
import { ApiOutlined } from '@ant-design/icons';
import { useApi } from '../../hooks/use_api';
import { useAuth } from '../../context/AuthContext';

const { Title, Text, Paragraph } = Typography;

// The OAuth parameters /oauth/authorize passed through; sent back unchanged.
const PASSED = ['client_id', 'redirect_uri', 'response_type', 'code_challenge', 'code_challenge_method', 'state'];

/** Consent page for a connector (e.g. the Claude app) asking to use this node
 * through its MCP server. Saying yes mints an agent token for it with the
 * choices made here, the same ones as Account → Agent tokens; the token then
 * shows up in that list and can be revoked there. The rules live in the
 * backend (services/oauth.py). */
function ConnectAgentPage() {
    const api = useApi();
    const { userPersonEntity } = useAuth();
    // Read once: the page is only ever entered from /oauth/authorize's redirect.
    const params = useMemo(() => {
        const q = new URLSearchParams(window.location.search);
        return Object.fromEntries(PASSED.map(k => [k, q.get(k)]));
    }, []);
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    const [groups, setGroups] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get('/api/oauth/request', { params: { client_id: params.client_id, redirect_uri: params.redirect_uri } })
            .then(setInfo)
            .catch(err => setError(err.message || 'This connection request is not valid.'));
    }, [api, params]);

    useEffect(() => {
        if (!userPersonEntity?.id) return;
        api.get(`/api/entities/${userPersonEntity.id}/connected`)
            .then(rows => setGroups(rows
                .filter(r => r.connected_status === true && ['context', 'relationship'].includes(r.entity.entity_type) && !r.entity.attributes?.is_dm)
                .map(r => ({ value: r.entity.id, label: r.entity.name }))))
            .catch(() => setGroups([]));
    }, [api, userPersonEntity?.id]);

    const answer = (path, values = {}) => {
        setBusy(true);
        api.post(path, {
            ...params,
            name: (values.name || '').trim(),
            can_write: !!values.can_write,
            context_id: values.context_id ?? null,
            expires_in_days: values.expires_in_days ?? 30,
        })
            .then(({ redirect_to }) => { window.location.assign(redirect_to); })
            .catch(err => {
                setError(err.message || 'Could not complete the connection.');
                setBusy(false);
            });
    };

    if (error) {
        return (
            <Card style={{ maxWidth: 560, margin: '24px auto' }}>
                <Alert type="error" showIcon message="Cannot connect" description={error} />
            </Card>
        );
    }
    if (!info) {
        return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;
    }

    return (
        <Card style={{ maxWidth: 560, margin: '24px auto' }}>
            <Space align="center" style={{ marginBottom: 8 }}>
                <ApiOutlined style={{ fontSize: 20 }} />
                <Title level={4} style={{ margin: 0 }}>Connect {info.client_name}</Title>
            </Space>
            <Paragraph>
                <Text strong>{info.client_name}</Text> wants to use this node as you, through its MCP server.
                After you approve, you'll be sent back to <Text code>{info.redirect_host}</Text>.
            </Paragraph>
            <Paragraph type="secondary">
                It gets an agent token with the limits below, not your login. A token can never change connections,
                invites, your account or node settings, everything it writes is logged, and you can revoke it any
                time in Account → Agent tokens.
            </Paragraph>
            <Form layout="vertical" onFinish={values => answer('/api/oauth/approve', values)}
                initialValues={{ name: info.client_name, can_write: false, expires_in_days: 30 }}>
                <Form.Item name="name" label="Name in your token list" rules={[{ required: true, whitespace: true, message: 'Name the connection' }]}>
                    <Input maxLength={80} />
                </Form.Item>
                <Form.Item name="can_write" label="May post messages and edit entities" valuePropName="checked">
                    <Switch />
                </Form.Item>
                <Form.Item name="context_id" label="Limit to one group (optional)">
                    <Select allowClear placeholder="Everything you can see" options={groups} showSearch optionFilterProp="label" />
                </Form.Item>
                <Form.Item name="expires_in_days" label="Expires after (days)" rules={[{ required: true }]}>
                    <InputNumber min={1} max={info.max_days} style={{ width: '100%' }} />
                </Form.Item>
                <Space>
                    <Button type="primary" htmlType="submit" loading={busy}>Approve</Button>
                    <Button onClick={() => answer('/api/oauth/deny')} disabled={busy}>Deny</Button>
                </Space>
            </Form>
        </Card>
    );
}

export default ConnectAgentPage;
